import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  FormControl, InputLabel, Select, MenuItem, Grid,
  Paper, Chip, Fade, Slide, Stack, CircularProgress
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, FileDownload as FileDownloadIcon,
  Public as PublicIcon, Lock as LockIcon, CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import TableComponent from '../../Components/TableComponent/TableComponent';
import { SnackBarComponent } from '../../Components/snackBar/snackBar';
import { API_BASE_URL } from '../../config';
import { LoadingSpinner } from '../../Components/spinner/spinner';
import api from '../../api/axiosInterceptor';

export const UserGrievanceComponent = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [formData, setFormData] = useState({
    Address: '',
    Description: '',
    complaint_type: '',
    complaint_regarding: '',
    complaint_regarding_other: '',
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [showOtherRegarding, setShowOtherRegarding] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteGrievance, setDeleteGrievance] = useState(null);

  // Dropdown options
  const complaintTypeOptions = [
    { value: 'Confidential', label: 'Confidential', icon: <LockIcon /> },
    { value: 'Open', label: 'Open', icon: <PublicIcon /> },
  ];

  const complaintRegardingOptions = [
    { value: 'Unauthorized Collection of Deposits', label: 'Un-authorized Collection of Deposits' },
    { value: 'Non repayment of Money collected from Various types of investment scheme', label: 'Non repayment of Money collected from Various types of investment scheme' },
    { value: 'Advertisements by unregistered entities soliciting offers for High rates of interest in investment', label: 'Advertisements by unregistered entities soliciting offers for High rates of interest in investment' },
    { value: 'Suspicious, Unauthorised Schemes floated by fraudulent companies or Individuals.', label: 'Suspicious, Unauthorised Schemes floated by fraudulent companies or Individuals.' },
    { value: 'Online Claim Application submission related', label: 'Online Claim Application submission related' },
    { value: 'Any Financial Establishment related', label: 'Any Financial Establishment related' },
    { value: 'Delayed Claim Application related', label: 'Delayed Claim Application related' },
    { value: 'others', label: 'Others' },
  ];

  // Enhanced status mapping
  const getStatusDisplay = (row) => {
    if (row.is_deletable) {
      return {
        label: 'Submitted',
        color: 'default',
        icon: null
      };
    }
    if (row.status === 'ac' || row.status === 'js' || row.status === 'sploca') {
      return {
        label: 'Resolved',
        color: 'success',
        icon: <CheckCircleIcon />
      };
    }
    if (row.status === 'rejected') {
      return {
        label: 'Rejected',
        color: 'error',
        icon: <CheckCircleIcon />
      };
    }
    return {
      label: 'Open',
      color: 'primary',
      icon: <PublicIcon />
    };
  };

  // Table columns
  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl No.', width: 80 },
    { id: 'akn_no', field: 'akn_no', headerName: 'Ack No.', width: 160 },
    { id: 'complaint_type', field: 'complaint_type', headerName: 'Type', width: 130 },
    { id: 'complaint_regarding', field: 'complaint_regarding', headerName: 'Regarding', width: 280, sortable: true },
    { id: 'Address', field: 'Address', headerName: 'Address', width: 280 },
    {
      id: 'doc',
      field: 'doc',
      headerName: 'Document',
      width: 140,
      renderCell: (params) => params.value ? (
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          href={params.value}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
        >
          Download
        </Button>
      ) : (
        <Chip label="No file" size="small" variant="outlined" color="default" />
      ),
    },
    {
      id: 'status',
      field: 'status',
      headerName: 'Status',
      width: 180,
      renderCell: (params) => {
        if (params.row.is_deletable) {
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label="Submitted"
                color="default"
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
              <IconButton 
                color="error" 
                onClick={() => handleDeleteConfirmation(params.row)}
                size="small"
                title="Delete"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          );
        }
        const status = getStatusDisplay(params.row);
        return (
          <Chip
            icon={status.icon}
            label={status.label}
            color={status.color}
            size="small"
            variant="filled"
          />
        );
      },
    },
  ];

  useEffect(() => {
    fetchGrievances();
  }, []);

  const fetchGrievances = () => {
    setLoading(true);
    api.get(`/grievance/public-grievances/`)
      .then(response => {
        const apiData = response.data.map((item, index) => ({
          id: item.id,
          slNo: index + 1,
          akn_no: item.akn_no,
          Address: item.Address,
          Description: item.Description || '',
          complaint_type: item.complaint_type || '',
          complaint_regarding: item.complaint_regarding || '',
          doc: item.doc,
          is_deletable: item.is_deletable || false,
          status: item.status,
        }));
        setRows(apiData);
        setLoading(false);
      })
      .catch(error => {
        console.error('Failed to fetch grievance data:', error);
        setSnackbarMessage('Failed to load grievance data.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleAddGrievance = () => {
    setFormData({ 
      Address: '', 
      Description: '', 
      complaint_type: '', 
      complaint_regarding: '',
      complaint_regarding_other: '',
    });
    setSelectedFile(null);
    setFormErrors({});
    setShowOtherRegarding(false);
    setOpenFormDialog(true);
  };

  const handleComplaintRegardingChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, complaint_regarding: value, complaint_regarding_other: '' });
    setShowOtherRegarding(value === 'others');
  };

  const handleDeleteConfirmation = (grievance) => {
    setDeleteGrievance(grievance);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api.delete(`/grievance/public-grievances/${deleteGrievance.id}/`)
      .then(() => {
        setSnackbarMessage('Grievance deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchGrievances();
      })
      .catch(error => {
        console.error('Delete failed:', error);
        setSnackbarMessage('Failed to delete grievance.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteGrievance(null);
      });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.Address?.trim()) errors.Address = 'Address is required';
    if (!formData.complaint_type) errors.complaint_type = 'Complaint type is required';
    
    const finalRegarding = formData.complaint_regarding === 'others' 
      ? formData.complaint_regarding_other 
      : formData.complaint_regarding;
    if (!finalRegarding?.trim()) errors.complaint_regarding = 'Complaint regarding is required';
    
    if (!selectedFile) errors.file = 'Document is required';
    if (selectedFile && selectedFile.size > 2 * 1024 * 1024) errors.file = 'File size must be less than 2MB';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 2 * 1024 * 1024) {
      setFormErrors(prev => ({ ...prev, file: 'File size must be less than 2MB' }));
      setSelectedFile(null);
    } else {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.file;
        return newErrors;
      });
      setSelectedFile(file);
    }
  };

  const handleFormSubmit = () => {
    if (!validateForm()) return;

    setSubmitLoading(true);

    const form = new FormData();
    form.append('Address', formData.Address);
    form.append('Description', formData.Description || '');
    form.append('complaint_type', formData.complaint_type);
    
    const finalRegarding = formData.complaint_regarding === 'others' 
      ? formData.complaint_regarding_other.trim() 
      : formData.complaint_regarding;
    form.append('complaint_regarding', finalRegarding);
    
    if (selectedFile) {
      form.append('doc', selectedFile);
    }

    api.post(`/grievance/public-grievances/`, form, { 
      headers: { 'Content-Type': 'multipart/form-data' }
    })
      .then(() => {
        setSnackbarMessage('Grievance submitted successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchGrievances();
      })
      .catch(error => {
        console.error('Add failed:', error);
        setSnackbarMessage('Failed to submit grievance.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setSubmitLoading(false);
        setOpenFormDialog(false);
        setFormData({ 
          Address: '', 
          Description: '', 
          complaint_type: '', 
          complaint_regarding: '',
          complaint_regarding_other: '',
        });
        setSelectedFile(null);
        setShowOtherRegarding(false);
      });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
        sx={{ backgroundColor: '#f5f5f5' }}
      >
        <CircularProgress size={40} thickness={4} />
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={600}>
      <Box
        sx={{
          width: '100%',
          mt: 3,
          mb: 3,
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            p: 3,
            borderRadius: '16px 16px 0 0',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ zIndex: 2, position: 'relative' }}>
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: '12px',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PublicIcon sx={{ color: 'white' }} />
            </Box>
            <Box sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 800, 
                  fontSize: '1.75rem',
                  color: 'white !important',
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                  lineHeight: 1.2
                }}
              >
                Grievances
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  opacity: 0.95,
                  fontWeight: 500,
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                }}
              >
                Manage your grievances and track status
              </Typography>
            </Box>
          </Stack>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddGrievance}
            size="large"
            sx={{
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.2,
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white',
              '&:hover': {
                background: 'rgba(255,255,255,0.3)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.25)',
              }
            }}
          >
            Lodge New Grievance
          </Button>
        </Paper>

        <Box p={3}>
          <TableComponent
            columns={columns}
            rows={rows}
            manageColumnStatus
            enableSort="true"
            sx={{
              borderRadius: 2,
              '& .MuiDataGrid-columnHeaders': {
                background: 'linear-gradient(90deg, #1976d2 0%, #2196f3 100%)',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.875rem',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
              }
            }}
          />
        </Box>

        {/* Form Dialog - unchanged */}
        <Dialog 
          open={openFormDialog} 
          onClose={() => setOpenFormDialog(false)} 
          maxWidth="sm" 
          fullWidth
          TransitionComponent={Slide}
          TransitionProps={{ direction: 'up' }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            fontWeight: 700,
            fontSize: '1.25rem',
            py: 3,
            px: 3,
            borderRadius: '16px 16px 0 0'
          }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ width: 45, height: 45, borderRadius: 2.5, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AddIcon sx={{ color: 'white' }} />
              </Box>
              Lodge New Grievance
            </Stack>
          </DialogTitle>
          
          <DialogContent sx={{ p: 3, pt: 2 }}>
            {/* 1. Complaint Type */}
            <Paper elevation={3} sx={{ p: 3, mb: 2, borderRadius: 3, border: '1px solid rgba(0,0,0,0.05)' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
                1. Complaint Type <span style={{ color: 'red' }}>*</span>
              </Typography>
              <FormControl fullWidth error={!!formErrors.complaint_type}>
                <InputLabel>Complaint Type</InputLabel>
                <Select
                  value={formData.complaint_type}
                  label="Complaint Type"
                  onChange={(e) => setFormData({ ...formData, complaint_type: e.target.value })}
                  sx={{ borderRadius: 2 }}
                >
                  {complaintTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        {option.icon}
                        <span>{option.label}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.complaint_type && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {formErrors.complaint_type}
                  </Typography>
                )}
              </FormControl>
            </Paper>

            {/* Rest of form fields remain exactly the same */}
            {/* 2. Complaint Regarding */}
            <Paper elevation={3} sx={{ p: 3, mb: 2, borderRadius: 3, border: '1px solid rgba(0,0,0,0.05)' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
                2. Complaint Regarding <span style={{ color: 'red' }}>*</span>
              </Typography>
              <FormControl fullWidth error={!!formErrors.complaint_regarding}>
                <InputLabel>Complaint Regarding</InputLabel>
                <Select
                  value={formData.complaint_regarding}
                  label="Complaint Regarding"
                  onChange={handleComplaintRegardingChange}
                  sx={{ borderRadius: 2 }}
                >
                  {complaintRegardingOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.complaint_regarding && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {formErrors.complaint_regarding}
                  </Typography>
                )}
              </FormControl>
            </Paper>

            {/* 2.1 Others Field (Conditional) */}
            {showOtherRegarding && (
              <Paper elevation={3} sx={{ p: 3, mb: 2, borderRadius: 3, border: '1px solid rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
                  2.1 Others (Specify) <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  label="Please specify the other complaint"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.complaint_regarding_other}
                  error={!!formErrors.complaint_regarding}
                  helperText={formErrors.complaint_regarding}
                  onChange={(e) => setFormData({ ...formData, complaint_regarding_other: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Paper>
            )}

            {/* 3. Address */}
            <Paper elevation={3} sx={{ p: 3, mb: 2, borderRadius: 3, border: '1px solid rgba(0,0,0,0.05)' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
                3. Address <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                label="Enter your complete address"
                fullWidth
                multiline
                rows={3}
                value={formData.Address}
                error={!!formErrors.Address}
                helperText={formErrors.Address}
                onChange={(e) => setFormData({ ...formData, Address: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Paper>

            {/* 4. Description */}
            <Paper elevation={3} sx={{ p: 3, mb: 2, borderRadius: 3, border: '1px solid rgba(0,0,0,0.05)' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
                4. Description
              </Typography>
              <TextField
                label="Provide additional details (Optional)"
                fullWidth
                multiline
                rows={4}
                value={formData.Description}
                onChange={(e) => setFormData({ ...formData, Description: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Paper>

            {/* 5. Document Upload */}
            <Paper elevation={3} sx={{ p: 3, mb: 2, borderRadius: 3, border: '1px solid rgba(0,0,0,0.05)' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
                5. Supporting Document <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload supporting documents (Max 2MB)
              </Typography>
              <Box>
                <input
                  accept="*/*"
                  style={{ display: 'none' }}
                  id="file-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<FileDownloadIcon />}
                    sx={{
                      width: '100%',
                      borderRadius: 2,
                      textTransform: 'none',
                      border: '2px dashed #1976d2',
                      borderColor: formErrors.file ? 'error.main' : '#1976d2',
                      color: formErrors.file ? 'error.main' : '#1976d2',
                      py: 1.5,
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: formErrors.file ? 'error.dark' : '#1565c0',
                        backgroundColor: formErrors.file ? 'error.50' : 'primary.50',
                      }
                    }}
                  >
                    {selectedFile ? `✅ ${selectedFile.name}` : 'Choose File'}
                  </Button>
                </label>
              </Box>
              {formErrors.file && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {formErrors.file}
                </Typography>
              )}
              {selectedFile && !formErrors.file && (
                <Chip 
                  label={`File selected: ${selectedFile.name}`} 
                  color="success" 
                  sx={{ mt: 1 }} 
                  variant="outlined"
                />
              )}
            </Paper>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 0, pb: 3 }}>
            <Button 
              onClick={() => setOpenFormDialog(false)} 
              disabled={submitLoading}
              sx={{ 
                borderRadius: 2, 
                textTransform: 'none', 
                px: 4, 
                py: 1.2,
                fontWeight: 500,
                border: '2px solid #757575'
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleFormSubmit} 
              variant="contained" 
              disabled={submitLoading}
              sx={{ 
                borderRadius: 2, 
                textTransform: 'none', 
                px: 4,
                py: 1.2,
                fontWeight: 600,
                fontSize: '1rem',
                boxShadow: '0 6px 20px rgba(25, 118, 210, 0.3)',
                background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(25, 118, 210, 0.4)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              {submitLoading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 600, color: 'error.main' }}>
            Confirm Delete
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this grievance? 
              <br />
              <Typography component="span" variant="body2" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                This action cannot be undone.
              </Typography>
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} variant="contained" color="error">
              Delete Grievance
            </Button>
          </DialogActions>
        </Dialog>

        <SnackBarComponent
          open={snackbarOpen}
          message={snackbarMessage}
          severity={snackbarSeverity}
          onClose={handleSnackbarClose}
        />
      </Box>
    </Fade>
  );
};
